import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Sparkles, Zap, Shield, Target, Award, Lock, Cloud } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { services, stats, testimonials } from '../data/mock';
import { PARTNER_LOGOS, CERTIFICATIONS, CLIENT_LOGOS } from '../data/logos';
import * as Icons from 'lucide-react';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const getIcon = (iconName) => {
    const Icon = Icons[iconName];
    return Icon ? <Icon className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden min-h-screen flex items-center">
        {/* Enhanced Gradient Background with depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-orange-50/30 to-white" />
        
        {/* Animated gradient orbs */}
        <div className="absolute top-20 right-10 w-96 h-96 bg-gradient-to-br from-orange-300/20 to-orange-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-to-br from-gray-300/20 to-gray-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Floating particles effect */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-orange-400 rounded-full animate-ping" style={{ animationDuration: '3s' }} />
          <div className="absolute top-1/3 right-1/3 w-2 h-2 bg-gray-400 rounded-full animate-ping" style={{ animationDuration: '4s', animationDelay: '1s' }} />
          <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-orange-300 rounded-full animate-ping" style={{ animationDuration: '5s', animationDelay: '2s' }} />
        </div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
        
        <div className="relative max-w-7xl mx-auto">
          <div className={`text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-md rounded-full text-sm font-medium text-gray-900 mb-8 shadow-lg border border-orange-200/50 hover:scale-105 transition-transform duration-300">
              <Sparkles className="w-4 h-4 text-orange-500 animate-pulse" />
              <span className="bg-gradient-to-r from-gray-900 to-orange-600 bg-clip-text text-transparent font-semibold">AI-First Enterprise Services</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              <span className="inline-block hover:scale-105 transition-transform duration-300">One Platform.</span><br />
              <span className="bg-gradient-to-r from-gray-900 via-gray-700 to-orange-500 bg-clip-text text-transparent inline-block hover:scale-105 transition-transform duration-300">
                Every Enterprise System.
              </span><br />
              <span className="inline-block hover:scale-105 transition-transform duration-300">AI-Led Execution.</span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
              Transform your enterprise with intelligent automation and unified solutions. 
              We make disconnected systems work like one organism.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact">
                <Button className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-6 text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  Start Your Transformation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/case-studies">
                <Button variant="outline" className="px-8 py-6 text-lg border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white rounded-xl transition-all duration-300">
                  View Case Studies
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Section with enhanced design */}
          <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = Icons[stat.icon];
              return (
                <div
                  key={index}
                  className="group relative text-center p-8 rounded-2xl bg-white/80 backdrop-blur-md border-2 border-gray-200/50 hover:border-orange-300 hover:shadow-2xl transition-all duration-500 hover:scale-110 hover:-translate-y-2"
                >
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-400/0 to-orange-600/0 group-hover:from-orange-400/20 group-hover:to-orange-600/10 rounded-2xl transition-all duration-500" />
                  
                  <div className="relative">
                    <div className="inline-flex p-3 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300">
                      {Icon && <Icon className="w-6 h-6 text-orange-600" />}
                    </div>
                    <div className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-orange-600 bg-clip-text text-transparent mb-2">{stat.value}</div>
                    <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Enterprises Choose AiFlo.ai
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Enterprises don't need more vendors. They need one intelligent partner.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: "AI-First Approach",
                description: "Every service powered by AI for 10x faster delivery and smarter outcomes.",
                color: "orange"
              },
              {
                icon: Target,
                title: "Single Accountability",
                description: "One partner, one platform, one point of contact. No more vendor fragmentation.",
                color: "gray"
              },
              {
                icon: Shield,
                title: "Platform Agnostic",
                description: "We recommend what's best for you, not what benefits us. Your success is our metric.",
                color: "orange"
              }
            ].map((item, index) => (
              <Card key={index} className="p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-orange-200 bg-white">
                <div className={`w-14 h-14 rounded-2xl bg-${item.color}-100 flex items-center justify-center mb-6`}>
                  <item.icon className={`w-7 h-7 text-${item.color}-600`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid - Enhanced */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-b from-white via-gray-50 to-white" />
        <div className="absolute top-20 left-0 w-72 h-72 bg-orange-300/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-0 w-72 h-72 bg-gray-300/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100/60 rounded-full text-sm font-medium text-orange-700 mb-4">
              <Zap className="w-4 h-4" />
              <span>Complete Enterprise Solutions</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Enterprise Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              End-to-end solutions from strategy to implementation and beyond.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card 
                key={service.id} 
                className="group p-8 hover:shadow-2xl transition-all duration-500 cursor-pointer border-2 border-transparent hover:border-orange-300 bg-white relative overflow-hidden"
              >
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-50/0 to-orange-100/0 group-hover:from-orange-50/30 group-hover:to-orange-100/20 transition-all duration-500" />
                
                <div className="relative">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
                    {getIcon(service.icon)}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <ul className="space-y-3 mb-6">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/services" className="inline-flex items-center gap-2 text-orange-600 font-medium group-hover:gap-3 transition-all">
                    Learn More
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-6 text-lg rounded-xl shadow-2xl hover:scale-105 hover:shadow-orange-500/20 transition-all duration-300">
                Explore All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials - Enhanced with modern design */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-orange-50/20 to-gray-50" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-300/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-md rounded-full text-sm font-medium text-orange-700 mb-4 shadow-lg">
              <Award className="w-4 h-4" />
              <span>Client Success Stories</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Enterprise Leaders
            </h2>
            <p className="text-xl text-gray-600">
              Real results from real partnerships
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={testimonial.id} className="group p-8 bg-white/90 backdrop-blur-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-transparent hover:border-orange-200 relative overflow-hidden">
                {/* Decorative quote mark */}
                <div className="absolute top-4 right-4 text-6xl text-orange-100 font-serif opacity-50">"</div>
                
                <div className="relative">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Sparkles key={i} className="w-5 h-5 text-orange-400 fill-orange-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed italic text-lg">"{testimonial.content}"</p>
                  <div className="border-t pt-6">
                    <p className="font-bold text-gray-900 text-lg">{testimonial.name}</p>
                    <p className="text-sm text-orange-600 font-medium">{testimonial.position}</p>
                    <p className="text-sm text-gray-500 mt-1">{testimonial.company}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Additional social proof */}
          <div className="mt-16 text-center">
            <Card className="inline-block p-8 bg-white/90 backdrop-blur-md border-2 border-orange-200">
              <div className="flex items-center gap-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-orange-600 mb-1">98%</div>
                  <div className="text-sm text-gray-600">Client Satisfaction</div>
                </div>
                <div className="w-px h-12 bg-gray-300" />
                <div className="text-center">
                  <div className="text-4xl font-bold text-orange-600 mb-1">4.9/5</div>
                  <div className="text-sm text-gray-600">Average Rating</div>
                </div>
                <div className="w-px h-12 bg-gray-300" />
                <div className="text-center">
                  <div className="text-4xl font-bold text-orange-600 mb-1">150+</div>
                  <div className="text-sm text-gray-600">Happy Clients</div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Enterprise Partners - Enhanced with real logos */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          {/* Certifications & Trust Badges */}
          <div className="text-center mb-16">
            <h3 className="text-sm font-semibold text-orange-600 mb-2 uppercase tracking-wider">Trusted & Certified</h3>
            <p className="text-gray-600">Industry-leading certifications and partnerships</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-20">
            {CERTIFICATIONS.map((cert, index) => {
              const Icon = Icons[cert.icon];
              return (
                <Card key={index} className="p-6 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border-2 border-transparent hover:border-orange-200 group">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    {Icon && <Icon className="w-6 h-6 text-orange-600" />}
                  </div>
                  <h4 className="font-bold text-gray-900 mb-1 text-sm">{cert.name}</h4>
                  <p className="text-xs text-gray-500">{cert.description}</p>
                </Card>
              );
            })}
          </div>

          <h3 className="text-center text-2xl font-bold text-gray-900 mb-4">
            Enterprise Technology Partners
          </h3>
          <p className="text-center text-gray-600 mb-12">
            Certified experts across leading enterprise platforms
          </p>
          
          {/* Enterprise Platforms */}
          <div className="mb-16">
            <h4 className="text-lg font-semibold text-gray-900 mb-6 text-center">ERP, CRM & Enterprise Applications</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6">
              {PARTNER_LOGOS.enterprise.map((partner, index) => (
                <div key={index} className="group relative">
                  <Card className="p-6 h-32 flex flex-col items-center justify-center text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white border-2 border-gray-100 hover:border-orange-300 cursor-pointer">
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-50/0 to-orange-100/0 group-hover:from-orange-50/50 group-hover:to-orange-100/30 rounded-lg transition-all duration-300" />
                    <div className="relative">
                      <div className="text-xl font-bold text-gray-900 mb-1 group-hover:text-orange-600 transition-colors">
                        {partner.name}
                      </div>
                      <div className="text-xs text-gray-500 group-hover:text-gray-700">{partner.category}</div>
                    </div>
                  </Card>
                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none z-10">
                    {partner.description}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Low-Code Platforms */}
          <div className="mb-16">
            <h4 className="text-lg font-semibold text-gray-900 mb-6 text-center">Low-Code / No-Code Platforms</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {PARTNER_LOGOS.lowCode.map((partner, index) => (
                <div key={index} className="group relative">
                  <Card className="p-6 h-32 flex flex-col items-center justify-center text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white border-2 border-gray-100 hover:border-orange-300 cursor-pointer">
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-50/0 to-orange-100/0 group-hover:from-orange-50/50 group-hover:to-orange-100/30 rounded-lg transition-all duration-300" />
                    <div className="relative">
                      <div className="text-base font-bold text-gray-900 mb-1 group-hover:text-orange-600 transition-colors">
                        {partner.name}
                      </div>
                      <div className="text-xs text-gray-500">{partner.category}</div>
                    </div>
                  </Card>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none z-10">
                    {partner.description}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cloud & Integration Platforms */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-6 text-center">Cloud Infrastructure</h4>
              <div className="grid grid-cols-2 gap-4">
                {PARTNER_LOGOS.cloud.map((partner, index) => (
                  <Card key={index} className="p-6 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border-2 border-gray-100 hover:border-orange-300">
                    <div className="font-bold text-gray-900 mb-1">{partner.name}</div>
                    <div className="text-xs text-gray-500">{partner.description}</div>
                  </Card>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-6 text-center">Integration Platforms</h4>
              <div className="grid grid-cols-2 gap-4">
                {PARTNER_LOGOS.integration.map((partner, index) => (
                  <Card key={index} className="p-6 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border-2 border-gray-100 hover:border-orange-300">
                    <div className="font-bold text-gray-900 mb-1">{partner.name}</div>
                    <div className="text-xs text-gray-500">{partner.description}</div>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          {/* Client Success Section */}
          <div className="mt-20">
            <h3 className="text-center text-2xl font-bold text-gray-900 mb-4">
              Trusted by Industry Leaders
            </h3>
            <p className="text-center text-gray-600 mb-12">
              Working with Fortune 500 companies and fast-growing enterprises
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {CLIENT_LOGOS.map((client, index) => (
                <Card key={index} className="p-6 text-center hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-gray-50 to-white border border-gray-200">
                  <div className="text-sm font-semibold text-gray-900 mb-1">{client.name}</div>
                  <div className="text-xs text-orange-600">{client.industry}</div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Ultra Modern */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
        
        {/* Animated gradient orbs */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-orange-500/30 to-orange-700/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-br from-orange-400/20 to-orange-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
        
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-sm font-medium text-orange-300 mb-8 border border-orange-500/20">
            <Sparkles className="w-4 h-4 animate-pulse" />
            <span>Start Your Transformation Journey</span>
          </div>
          
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
            Ready to Transform Your Enterprise?
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Let's discuss how we can accelerate your digital transformation journey with our AI-first approach.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/contact">
              <Button className="group bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white px-8 py-6 text-lg rounded-xl shadow-2xl hover:scale-105 hover:shadow-orange-500/50 transition-all duration-300 border-2 border-orange-300/20">
                Schedule a Consultation
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/case-studies">
              <Button variant="outline" className="bg-white/10 backdrop-blur-md text-white border-2 border-white/20 hover:bg-white/20 px-8 py-6 text-lg rounded-xl transition-all duration-300 hover:scale-105">
                View Success Stories
              </Button>
            </Link>
          </div>
          
          {/* Trust indicators */}
          <div className="mt-12 flex flex-wrap justify-center items-center gap-8 text-gray-400 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-orange-400" />
              <span>Free consultation</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-orange-400" />
              <span>No obligation</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-orange-400" />
              <span>24-hour response</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
